import React from 'react';
import PropTypes from 'prop-types';
import {
  Grid, Card, CardContent, Typography, Box, LinearProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Button
} from '@mui/material';
import { TrendingUp, AccountBalance, Build, CheckCircle, RocketLaunch } from '@mui/icons-material';

const DEFAULT_ONBOARDING_STEPS = [
  { key: 'credentialsProvided', label: 'Credentials Provided (Client)' },
  { key: 'hl7Form', label: 'HL7 Retrieval Form Submission' },
  { key: 'hl7Config', label: 'HL7 Configuration in Servers' },
  { key: 'hl7Arrival', label: 'HL7 Arrival in the Servers' },
  { key: 'evokePortal', label: 'Visible in Evoke Portal - Reports' },
  { key: 'rpaSetup', label: 'Setup Manufacturer Crawler RPA' },
  { key: 'twilioNext', label: 'Twilio Call/SMS - Next Send' },
  { key: 'twilioFirst', label: 'Twilio Call/SMS - First Reminder' },
  { key: 'twilioSecond', label: 'Twilio Call/SMS - Second Reminder' },
  { key: 'twilioThird', label: 'Twilio Call/SMS - Third Reminder' },
  { key: 'postGrid', label: 'PostGrid - No Show Letter' },
  { key: 'qaVerification', label: 'QA Verification - Reports Transmitted' }
];

function OnboardingDashboard({ onboardingData = [], onFilterAccounts }) {
  const getManufacturerStats = () => {
    const stats = {};
    
    if (!Array.isArray(onboardingData) || onboardingData.length === 0) return stats;
    
    onboardingData.forEach(account => {
      if (!account.manufacturers) return;
      
      account.manufacturers.forEach(manufacturer => {
        if (!stats[manufacturer]) {
          stats[manufacturer] = { total: 0, completed: 0, inProgress: 0, blocked: 0, totalProgress: 0 };
        }
        stats[manufacturer].total++;
        
        const progress = calculateProgress(account, manufacturer);
        stats[manufacturer].totalProgress += progress;
        
        if (Math.round(progress) === 100) {
          stats[manufacturer].completed++;
        } else if (progress > 0) {
          stats[manufacturer].inProgress++;
        }
        
        const manufacturerData = account.manufacturerData?.[manufacturer];
        if (manufacturerData?.blocker && manufacturerData.blocker.trim() !== '') {
          stats[manufacturer].blocked++;
        }
      });
    });
    
    // Calculate average progress for each manufacturer
    Object.keys(stats).forEach(manufacturer => {
      stats[manufacturer].averageProgress = stats[manufacturer].total > 0 
        ? stats[manufacturer].totalProgress / stats[manufacturer].total 
        : 0;
    });
    
    return stats;
  };

  const isStepCompleted = (status, stepKey) => {
    if (stepKey === 'evokePortalName') {
      return status && status.trim() !== '';
    }
    const trimmedStatus = (status || '').trim();
    // Decode HTML entities for proper comparison
    const decodedStatus = trimmedStatus.replace(/&lt;/g, '<').replace(/&gt;/g, '>');
    return decodedStatus === 'Done' || 
           decodedStatus === 'Not Required' || 
           decodedStatus === 'Not Available' ||
           decodedStatus.startsWith('Sent By') || 
           decodedStatus.startsWith('Submitted By');
  };

  const isDataFieldCompleted = (value, fieldKey) => {
    if (fieldKey === 'remarks') return false; // Don't count remarks for metrics
    return value && value.trim() !== '';
  };

  const calculateProgress = (account, manufacturer = null) => {
    const steps = DEFAULT_ONBOARDING_STEPS.filter(step => 
      step.key !== 'credentials' && step.key !== 'goLiveDate'
    );
    const dataFields = ['credentials']; // Manufacturer-level fields only
    
    if (manufacturer) {
      const mfgData = account.manufacturerData[manufacturer] || {};
      
      // Count completed steps (10 steps)
      const completedSteps = steps.filter(step => 
        isStepCompleted(mfgData[step.key] || '', step.key)
      ).length;
      
      // Count completed manufacturer data fields (1 field: credentials)
      const completedMfgFields = dataFields.filter(field => 
        isDataFieldCompleted(mfgData[field] || '', field)
      ).length;
      
      // Count account-level fields (2 fields: evokePortalName, goLiveDate)
      const completedAccountFields = (
        (isDataFieldCompleted(account.evokePortalName || '', 'evokePortalName') ? 1 : 0) +
        (isDataFieldCompleted(account.goLiveDate || '', 'goLiveDate') ? 1 : 0)
      );
      
      const totalItems = steps.length + dataFields.length + 2; // 10 steps + 1 mfg field + 2 account fields = 13
      const completedItems = completedSteps + completedMfgFields + completedAccountFields;
      
      return totalItems > 0 ? (completedItems / totalItems) * 100 : 0;
    }
    
    if (!account.manufacturers?.length) return 0;
    
    // For overall account progress, calculate average across all manufacturers
    const totalProgress = account.manufacturers.reduce((sum, mfg) => {
      return sum + calculateProgress(account, mfg);
    }, 0);
    
    return totalProgress / account.manufacturers.length;
  };

  const getOverallStats = () => {
    if (!Array.isArray(onboardingData) || onboardingData.length === 0) {
      return { total: 0, completed: 0, inProgress: 0, notStarted: 0, goLive: 0 };
    }
    
    const total = onboardingData.length;
    const completed = onboardingData.filter(account => {
      const progress = calculateProgress(account);
      return Math.round(progress) === 100;
    }).length;
    
    const inProgress = onboardingData.filter(account => {
      const progress = calculateProgress(account);
      return progress > 0 && Math.round(progress) < 100;
    }).length;
    
    const goLive = onboardingData.filter(account => account.goLiveStatus === true).length;
    
    const notStarted = total - completed - inProgress;
    
    return { total, completed, inProgress, notStarted, goLive };
  };

  const getRecentActivity = () => {
    const upcomingDates = [];
    
    if (!Array.isArray(onboardingData) || onboardingData.length === 0) return upcomingDates;
    
    onboardingData.forEach(account => {
      if (!account.manufacturers || !account.goLiveDate) return;
      
      const goLiveDate = account.goLiveDate;
      const progress = calculateProgress(account);
      
      if (goLiveDate && goLiveDate.trim() !== '' && account.goLiveStatus !== true) {
        upcomingDates.push({
          ...account,
          goLiveDate,
          progress
        });
      }
    });
    
    return upcomingDates
      .toSorted((a, b) => new Date(a.goLiveDate) - new Date(b.goLiveDate));
  };

  const getGoLiveAccounts = () => {
    if (!Array.isArray(onboardingData) || onboardingData.length === 0) return [];
    
    return onboardingData
      .filter(account => account.goLiveStatus === true)
      .sort((a, b) => {
        // Sort by go-live date (most recent first), then by creation date, then by name
        if (a.goLiveDate && b.goLiveDate) {
          const dateA = new Date(a.goLiveDate);
          const dateB = new Date(b.goLiveDate);
          if (dateA.getTime() !== dateB.getTime()) {
            return dateB - dateA; // Most recent first
          }
        }
        if (a.createdDate && b.createdDate) {
          const createdA = new Date(a.createdDate);
          const createdB = new Date(b.createdDate);
          if (createdA.getTime() !== createdB.getTime()) {
            return createdB - createdA; // Most recent first
          }
        }
        return a.accountName.localeCompare(b.accountName);
      });
  };

  const manufacturerStats = getManufacturerStats();
  const overallStats = getOverallStats();
  const recentActivity = getRecentActivity();
  const goLiveAccounts = getGoLiveAccounts();

  return (
    <Box>

      {/* Overall Statistics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <Card 
            sx={{ 
              cursor: 'pointer', 
              transition: 'all 0.2s ease-in-out',
              '&:hover': { 
                boxShadow: 4, 
                transform: 'translateY(-2px)' 
              } 
            }} 
            onClick={() => {
              if (onFilterAccounts && typeof onFilterAccounts === 'function') {
                onFilterAccounts('all');
              }
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AccountBalance color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Accounts
                  </Typography>
                  <Typography variant="h4">
                    {overallStats.total}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <Card 
            sx={{ 
              cursor: 'pointer', 
              transition: 'all 0.2s ease-in-out',
              '&:hover': { 
                boxShadow: 4, 
                transform: 'translateY(-2px)' 
              } 
            }} 
            onClick={() => {
              if (onFilterAccounts && typeof onFilterAccounts === 'function') {
                onFilterAccounts('completed');
              }
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CheckCircle color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Completed
                  </Typography>
                  <Typography variant="h4">
                    {overallStats.completed}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <Card 
            sx={{ 
              cursor: 'pointer', 
              transition: 'all 0.2s ease-in-out',
              '&:hover': { 
                boxShadow: 4, 
                transform: 'translateY(-2px)' 
              } 
            }} 
            onClick={() => {
              if (onFilterAccounts && typeof onFilterAccounts === 'function') {
                onFilterAccounts('inProgress');
              }
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Build color="warning" sx={{ mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    In Progress
                  </Typography>
                  <Typography variant="h4">
                    {overallStats.inProgress}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TrendingUp color="info" sx={{ mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Average Progress
                  </Typography>
                  <Typography variant="h4">
                    {overallStats.total > 0 ? Math.round(onboardingData.reduce((sum, account) => sum + calculateProgress(account), 0) / overallStats.total) : 0}%
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Manufacturer Breakdown */}
        <Grid
          size={{
            xs: 12,
            md: 8
          }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Manufacturer Progress
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Manufacturer</TableCell>
                      <TableCell align="center">Total</TableCell>
                      <TableCell align="center">Completed</TableCell>
                      <TableCell align="center">In Progress</TableCell>
                      <TableCell align="center">Blocked</TableCell>
                      <TableCell>Progress</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(manufacturerStats).map(([manufacturer, stats]) => {
                      const averageProgress = Math.round(stats.averageProgress);
                      return (
                        <TableRow key={manufacturer}>
                          <TableCell>{manufacturer}</TableCell>
                          <TableCell align="center">{stats.total}</TableCell>
                          <TableCell align="center">
                            <Chip label={stats.completed} color="success" size="small" />
                          </TableCell>
                          <TableCell align="center">
                            <Chip label={stats.inProgress} color="warning" size="small" />
                          </TableCell>
                          <TableCell align="center">
                            <Chip label={stats.blocked} color="error" size="small" />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <LinearProgress
                                variant="determinate"
                                value={averageProgress}
                                sx={{ flexGrow: 1, mr: 1 }}
                              />
                              <Typography variant="body2">
                                {averageProgress}%
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Activity */}
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Upcoming Go-Live Dates
              </Typography>
              {recentActivity.length > 0 ? (
                recentActivity.slice(0, 5).map((account) => {
                  const today = new Date();
                  const goLiveDate = new Date(account.goLiveDate);
                  const isOverdue = goLiveDate < today;
                  
                  return (
                    <Box key={account.id} sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="subtitle2" noWrap>
                        {account.accountName}
                      </Typography>
                      <Typography variant="body2" color="primary">
                        {isOverdue && '⚠️ '}{new Date(account.goLiveDate).toLocaleDateString()}
                        {isOverdue && (
                          <Typography
                            component="span"
                            sx={{
                              color: "error.main",
                              fontWeight: 'medium'
                            }}>
                            {' (Overdue)'}
                          </Typography>
                        )}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={account.progress}
                        sx={{ mt: 1 }}
                      />
                    </Box>
                  );
                })
              ) : (
                <Typography sx={{
                  color: "text.secondary"
                }}>
                  No upcoming go-live dates scheduled
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Go-Live Section */}
      {goLiveAccounts.length > 0 && (
        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid size={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <RocketLaunch color="secondary" />
                    Go-Live Accounts ({goLiveAccounts.length})
                  </Typography>
                  {goLiveAccounts.length > 5 && (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Typography variant="body2" sx={{
                        color: "text.secondary"
                      }}>
                        Showing 5 of {goLiveAccounts.length}
                      </Typography>
                      <Box
                        component="button"
                        onClick={() => {
                          if (onFilterAccounts && typeof onFilterAccounts === 'function') {
                            onFilterAccounts('goLive');
                          }
                        }}
                        sx={{
                          background: 'none',
                          border: '1px solid',
                          borderColor: 'primary.main',
                          borderRadius: 1,
                          px: 2,
                          py: 0.5,
                          color: 'primary.main',
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          '&:hover': {
                            backgroundColor: 'primary.50'
                          }
                        }}
                      >
                        View All
                      </Box>
                    </Box>
                  )}
                </Box>
                <Grid container spacing={2}>
                  {goLiveAccounts.slice(0, 5).map((account) => (
                    <Grid
                      key={account.id}
                      size={{
                        xs: 12,
                        sm: 6,
                        md: 4
                      }}>
                      <Box sx={{ p: 2, bgcolor: 'secondary.50', borderRadius: 1, border: '1px solid', borderColor: 'secondary.200' }}>
                        <Typography variant="subtitle2" noWrap sx={{
                          fontWeight: 600
                        }}>
                          {account.accountName}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                            mb: 1
                          }}>
                          {account.manufacturers.join(', ')}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={calculateProgress(account)}
                            sx={{ flexGrow: 1, height: 6, borderRadius: 3 }}
                          />
                          <Typography variant="body2" sx={{
                            color: "text.secondary"
                          }}>
                            {Math.round(calculateProgress(account))}%
                          </Typography>
                        </Box>
                        {account.goLiveDate && (
                          <Typography variant="body2" color="primary" sx={{ mt: 1, fontSize: '0.8rem' }}>
                            Go-Live: {new Date(account.goLiveDate).toLocaleDateString()}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}

OnboardingDashboard.propTypes = {
  onboardingData: PropTypes.array.isRequired,
  onFilterAccounts: PropTypes.func.isRequired
};

export default OnboardingDashboard;